import { Injectable } from '@angular/core';
import { JITMeetConferenceGateway } from '../../../domain/gateways/jit-meet/jit-meet-conference.gateway';
import { JITMeetConferenceSession } from '../../../domain/gateways/jit-meet/jit-meet-conference-session';
import {
  JITMeetConferenceAccess,
  JITMeetConferenceOptions,
} from '../../../domain/models/jit-meet/conference/jit-meet-conference.model';
import { JIT_MEET_DEFAULT_CONFERENCE_OPTIONS } from './jitsi-meet.config';
import { JitsiMeetConferenceSession } from './jitsi-meet-conference.session';
import { JitsiScriptLoaderService } from './jitsi-script-loader.service';

@Injectable()
export class JitsiMeetConferenceGateway extends JITMeetConferenceGateway {
  constructor(private readonly scriptLoader: JitsiScriptLoaderService) {
    super();
  }

  async createSession(
    container: HTMLElement,
    access: JITMeetConferenceAccess,
    options?: JITMeetConferenceOptions
  ): Promise<JITMeetConferenceSession> {
    this.validate(container, access);
    await this.scriptLoader.load(access.domain);

    const JitsiMeetExternalAPI = window.JitsiMeetExternalAPI;

    if (!JitsiMeetExternalAPI) {
      throw new Error('La API de JIT Meet no está disponible.');
    }

    const mergedOptions = this.mergeOptions(options);
    const api = new JitsiMeetExternalAPI(
      this.normalizeHost(access.domain),
      {
        roomName: access.roomName,
        jwt: access.jwt,
        parentNode: container,
        width: mergedOptions.width,
        height: mergedOptions.height,
        lang: mergedOptions.language,
        configOverwrite: mergedOptions.configOverwrite,
        interfaceConfigOverwrite: mergedOptions.interfaceConfigOverwrite,
        userInfo: {
          displayName: access.participantName,
          email: access.participantEmail,
        },
      }
    );

    return new JitsiMeetConferenceSession(api);
  }

  private mergeOptions(
    options?: JITMeetConferenceOptions
  ): JITMeetConferenceOptions {
    const defaults = JIT_MEET_DEFAULT_CONFERENCE_OPTIONS;

    return {
      ...defaults,
      ...options,
      configOverwrite: {
        ...defaults.configOverwrite,
        startWithAudioMuted: options?.audioMuted ?? defaults.audioMuted,
        startWithVideoMuted: options?.videoMuted ?? defaults.videoMuted,
        ...options?.configOverwrite,
      },
      interfaceConfigOverwrite: {
        ...defaults.interfaceConfigOverwrite,
        ...options?.interfaceConfigOverwrite,
      },
    };
  }

  private validate(
    container: HTMLElement,
    access: JITMeetConferenceAccess
  ): void {
    if (!container) {
      throw new Error('No se encontró el contenedor de JIT Meet.');
    }

    if (!access.domain?.trim() || !access.roomName?.trim() || !access.jwt?.trim()) {
      throw new Error('El dominio, la sala y el JWT de JIT Meet son obligatorios.');
    }
  }

  private normalizeHost(domain: string): string {
    const normalized = /^https?:\/\//i.test(domain.trim())
      ? domain.trim()
      : `https://${domain.trim()}`;

    return new URL(normalized).host;
  }
}
